/**
 * JavaScript content type view component.
 * @module components/theme/View/JavaScriptView
 */

import { useState } from 'react';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import { addPrefixPath } from '@serpro/volto-prefixpath/helpers/Url/Url';
import PropTypes from 'prop-types';
import { defineMessages, injectIntl } from 'react-intl';
import { Container } from 'semantic-ui-react';

const messages = defineMessages({
  fileUrl: {
    id: 'File URL',
    defaultMessage: 'File URL',
  },
  copyUrl: {
    id: 'Copy URL',
    defaultMessage: 'Copy URL',
  },
  copied: {
    id: 'Copied!',
    defaultMessage: 'Copied!',
  },
  scriptTagHelper: {
    id: 'Use this URL as the src attribute in a <script src="..."> tag.',
    defaultMessage:
      'Use this URL as the src attribute in a <script src="..."> tag.',
  },
  cacheChangeWarning: {
    id: 'If the file is changed, the URL in the destination must be updated.',
    defaultMessage:
      'If the file is changed, the URL in the destination must be updated.',
  },
});

/**
 * Check if the current user has edit permission based on available actions.
 * @param {object} content - The content object.
 * @returns {boolean} True if user can edit.
 */
const canEdit = (content) => {
  const actions = content?.['@components']?.actions?.object ?? [];
  return actions.some((action) => action.id === 'edit');
};

/**
 * Hash curto (djb2, base36) usado para versionar a URL e forçar cache-busting.
 * @param {string} value - Valor de entrada (ex.: data de modificação).
 * @returns {string} Hash curto em base36, ou string vazia se `value` for vazio.
 */
const shortHash = (value) => {
  if (!value) return '';
  let hash = 5381;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 33) ^ value.charCodeAt(i);
  }
  return (hash >>> 0).toString(36);
};

/**
 * Monta o nome de arquivo versionado, inserindo o hash de `modified` antes da extensão .js.
 * @param {object} content - Content object.
 * @returns {string} Nome de arquivo versionado (ex.: script.a1b2c3.js).
 */
const buildVersionedFilename = (content) => {
  const filename = content?.file?.filename;
  const base = filename
    ? filename.replace(/\.js$/i, '')
    : content?.['@id']?.split('/').pop();
  const hash = shortHash(content?.modified);
  return hash ? `${base}.${hash}.js` : `${base}.js`;
};

/**
 * JavaScript view component.
 * Shows only the title for anonymous users.
 * Shows the title, download URL, and a copy button for editors.
 * @function JavaScriptView
 * @param {object} content Content object.
 * @param {object} intl Intl object.
 * @returns {string} Markup of the component.
 */
const JavaScriptView = ({ content, intl }) => {
  const [copied, setCopied] = useState(false);
  const isEditor = canEdit(content);
  const hasFile = Boolean(content.file?.filename);
  const downloadPath = addPrefixPath(
    flattenToAppURL(content['@id']) +
      '/@@download/file/' +
      buildVersionedFilename(content),
  );
  const downloadUrl =
    (typeof window !== 'undefined' ? window.location.origin : '') +
    downloadPath;

  const handleCopy = () => {
    navigator.clipboard.writeText(downloadUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Container className="view-wrapper mb-3xh">
      <h1 className="documentFirstHeading">{content.title}</h1>
      {content.description && (
        <p className="documentDescription">{content.description}</p>
      )}

      {isEditor && hasFile && (
        <div className="mb-3x">
          <h2 className="text-up-02">{intl.formatMessage(messages.fileUrl)}</h2>
          <code className="d-block mb-2" style={{ wordBreak: 'break-all' }}>
            {downloadUrl}
          </code>
          <button
            className="br-button secondary small mt-1"
            onClick={handleCopy}
            type="button"
          >
            {copied
              ? intl.formatMessage(messages.copied)
              : intl.formatMessage(messages.copyUrl)}
          </button>
          <p className="text-down-01 mt-2" style={{ color: '#555' }}>
            {intl.formatMessage(messages.scriptTagHelper)}
          </p>
          <p className="text-down-01 mt-1" style={{ color: '#a94442' }}>
            {intl.formatMessage(messages.cacheChangeWarning)}
          </p>
        </div>
      )}
    </Container>
  );
};

JavaScriptView.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    '@id': PropTypes.string,
    '@components': PropTypes.object,
    modified: PropTypes.string,
    file: PropTypes.shape({
      filename: PropTypes.string,
    }),
  }).isRequired,
  intl: PropTypes.object.isRequired,
};

export default injectIntl(JavaScriptView);
